<template>
    <div class="d-flex flex-column flex-fill justify-content-between">
        <div class="card-data">
            <h4 class="card-data__title" v-if="isCredCard" v-text="$ml.get('titleViewCredCard')" tabindex="0" />
            <h4 class="card-data__title" v-else-if="isMultibenefits" v-text="$ml.get('multibenefits')" tabindex="0" />
            <h4 class="card-data__title" v-else v-text="$ml.get('titleViewDebitCard')" tabindex="0" />

            <div class="card-data__input-container">
                <CieloCardInputV2
                    :card-brand="paymentMethodImage"
                    :card-number="cardNumber"
                    @input="setCardNumber"
                    :error-card="errors.first('CreditCardNumber') || cardError"
                    :is-valid-errors="isValidErrors"
                />
            </div>

            <div class="card-data__input-container">
                <CieloInputV2
                    :autofocus="true"
                    :borderless="false"
                    name="Holder"
                    :label="$ml.get('inputCardHolderName')"
                    v-model="holder"
                    v-validate="'required|fullname'"
                    :data-vv-as="$ml.get('inputCardHolderName')"
                    type="text"
                    :error="errors.first('Holder')"
                />
            </div>
            <div class="card-data__container-security-card">
                <CieloInputV2
                    :autofocus="true"
                    :borderless="false"
                    name="ValidDate"
                    :label="$ml.get('inputCardDueDate')"
                    v-model="validDate"
                    v-validate="'required|min:4'"
                    :data-vv-as="$ml.get('expiration')"
                    :error="errors.first('ValidDate') || expiryError"
                    type="tel"
                    :mask="['##/##']"
                />
                <CieloInputV2
                    :autofocus="false"
                    :borderless="false"
                    name="SecurityCode"
                    :label="$ml.get('inputCVV')"
                    v-model="securityCode"
                    v-validate="textValidedCvv"
                    :data-vv-as="$ml.get('securityCode')"
                    :type="'tel'"
                    :error="errors.first('SecurityCode')"
                    :mask="[maskValidedCvv]"
                />
            </div>
            <div v-if="isCredCard && !recurringPayment" class="card-data__input-container">
                <CieloSelectV2
                    @change="selectItem"
                    :title="$ml.get('installment')"
                    name="selectInstallment"
                    :label="$ml.get('installmentsNumber')"
                    :option-items="installmentList || undefined"
                    :selected-item="numberOfInstallments"
                    :on-get-item-event="getLangOptions"
                    :disabled="!paymentMethodImage || this.installmentIsFixed"
                />
                <InstallmentWarning />
            </div>

            <RecurringPaymentWarning :stripe="true" />

            <div class="card-data__billing-container">
                <h4 class="card-data__title" tabindex="0">
                    {{ $ml.get('billingAddress') }}
                </h4>
                <SameShippingAddress v-if="productHasShipping" :same-as-shipping="true" />
                <BillingAddress :address-errors="addressErrors" />
            </div>
        </div>

        <ButtonsNextPreviousV2
            v-if="!isRecurringPayment"
            :disable-button-next="disableButtonNext"
            @click-next="goToNextStep"
            @click-previous="goToPreviousStep"
        />
        <CieloButtonV2
            v-else
            :disabled="disableButtonNext"
            @click="goToNextStep"
        >
            <span v-text="$ml.get('continue')" />
        </CieloButtonV2>
    </div>
</template>

<script>
	import Swal from 'sweetalert2';

	import ProductHelper from '@/helpers/productHelper';
	import { logEvent } from '@/helpers/cieloLogger';
	import loader from '@/helpers/loader';
	import { Token, Options } from '@/config/lang/genericOptions.js';

	import CardHolderNameValidation from '@/helpers/validation-rules/CardHolderNameValidation';

	import { mapGetters } from 'vuex';

	const CieloInputV2 = () =>
		import(
			/* webpackChunkName: "cielo-input-v2" */
			'@/components/ClickToPay/template/CieloInputV2'
		);
	const CieloCardInputV2 = () =>
		import(
			/* webpackChunkName: "cielo-input-v2" */
			'@/components/template/CieloCardInputV2'
		);
	import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2';
	import RecurringPaymentWarning from '@/components/RecurringPaymentWarning';
	import SameShippingAddress from '@/components/Billing/SameShippingAddress';
	import BillingAddress from '@/components/Billing/BillingAddress';
	import PaymentTypes from '@/models/PaymentTypes';
	import ButtonsNextPreviousV2 from '@/components/template/ButtonsNextPreviousV2';
	import CieloButtonV2 from '@/components/template/CieloButtonV2.vue';
	import InstallmentWarning from '@/components/InstallmentWarning';
	

	export default {
		components: {
			CieloInputV2,
			CieloCardInputV2,
			CieloSelectV2,
			RecurringPaymentWarning,
			ButtonsNextPreviousV2,
			CieloButtonV2,
			InstallmentWarning,
			SameShippingAddress,
			BillingAddress
		},

		computed: {
			...mapGetters({
				installmentList: 'payment/getInstallmentListForPaymentMethodSelected',
				selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
				paymentType: 'payment/getSelectedPaymentTypeId',
				recurringPayment: 'recurrentPayment/isRecurringPayment',
				installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed',
				billingAddress: 'billingAddress/getBillingAddress',
				sameAsShippingAddress: 'billingAddress/getSameAsShippingAddress',
				isNational: 'billingAddress/getIsNational',
			}),

			isCredCard: (self) =>
				self.$store.state.payment.PaymentType == PaymentTypes.CREDIT_CARD,

			isMultibenefits: (self) =>
      			self.$store.state.payment.PaymentType == PaymentTypes.MULTIBENEFITS,

			isRecurringPayment: (self) =>
				self.recurringPayment == true,

			paymentMethodImage: (self) => {
				return self.selectedPaymentMethod ? self.selectedPaymentMethod.Image : null;
			},

			productHasShipping: () => ProductHelper.productHasShipping(),

			textValidedCvv: (self) => {
				if (self.selectedPaymentMethod) {
					return self.selectedPaymentMethod.Image == 'amex' ? 'required|min:4|max:4' : 'required|min:3|max:4';
				}
			},

			maskValidedCvv: (self) => {
				if (self.selectedPaymentMethod) {
					return self.selectedPaymentMethod.Image == 'amex' ? '####' : '###';
				}
			},

			numberOfInstallments: {
				get: (self) =>
					self.$store.getters['payment/getInstallmentNumberOfPayments'],
				set(value) {
					this.$store.commit('payment/setNumberOfInstallments', value);
				},
			},
			cardNumber: {
				get: (self) => self.$store.state.cardPayment.CardNumber || '',
				set(value) {
					this.$store.commit('cardPayment/setCardNumber', value);
				},
			},
			holder: {
				get: (self) => self.$store.state.cardPayment.Holder,
				set(value) {
					this.$store.commit('cardPayment/setHolder', value);
				},
			},
			validDate: {
				get() {
					let month = this.$store.state.cardPayment.Expiration.Month;
					let year = this.$store.state.cardPayment.Expiration.Year;

					return `${month}${year}`;
				},

				set(value) {
					const values = value.split('');
					const month = `${values[0]}${values[1]}`;
					const year = `${values[2]}${values[3]}`;
					let expirationMonth = month >= 0 ? month : '';
					let expirationYear = year >= 2 ? year : '';

					let today = new Date();
					let cardExpiration = new Date(`20${year}`, month);

					if (month < 1 || month > 12) {
					this.expiryError = this.$ml.get('dateMonthCardError');
					}
					else if (cardExpiration < today) {
					this.expiryError = this.$ml.get('cardExpirationError');
					} else {
					this.expiryError = '';
					}

					this.$store.commit('cardPayment/setExpirationMonth', expirationMonth);
					this.$store.commit('cardPayment/setExpirationYear', expirationYear);
				},
			},

			securityCode: {
				get: (self) => self.$store.state.cardPayment.SecurityCode,
				set(value) {
					this.$store.commit('cardPayment/setSecurityCode', value);
				},
			},

			binQueryError: {
				get: (self) => self.$store.state.cardPayment.BinQueryError,
			},

			isBillingFormInvalid() {

				if(this.sameAsShippingAddress) return false;

				const address = this.billingAddress;
				
				if (this.isNational) {
					return (
						!address.Street ||
						!address.State ||
						!address.City ||
						!address.Number ||
						!address.District ||
						!address.ZipCode
					);
				} else {
					return (
						!address.Street ||
						!address.State ||
						!address.City ||
						!address.ZipCode
					);
				}
			},

			disableButtonNext() {
				const isNotCredCardRecurring = this.isCredCard && !this.recurringPayment;
				const missingCommonFields = !this.cardNumber || !this.validDate || !this.holder || !this.securityCode;
				const missingBillingAddressFields = this.isBillingFormInvalid;
				const hasErrors = this.errors.items.length > 0 || this.cardError || this.expiryError;

				if (isNotCredCardRecurring) {
					return hasErrors || missingCommonFields || missingBillingAddressFields || this.numberOfInstallments == 0;
				}
				return hasErrors || missingCommonFields || missingBillingAddressFields;
			},
		},


		mounted() {
			if (this.cardNumber) {
				this.doBinQuery();
			}
			loader.multiLanguage = this.$ml;

			if (this.securityCode || this.validDate || this.holder || this.cardNumber) {
				this.$validator.validate();
				this.isValidErrors = true;
			}
			this.$validator.extend('fullname', CardHolderNameValidation);

			if (this.numberOfInstallments) this.itemSelected = true;
		},

		data() {
			return {
				showNextPreviousButton: false,
				expiryError: '',
				cardError: '',
				isValidErrors: false,
				addressErrors: {},
			};
		},
		
		methods: {
			setCardNumber(value) {
				const oldBin = this.cardNumber.substring(0, 8);
				const newBin = value.substring(0, 8);
				this.cardNumber = value;

				if (value && value.length >= 8 && newBin !== oldBin) {
					this.doBinQuery();
				} else if (value && value.length <= 8 || !value) {
					this.$store.commit('payment/resetMethod', null);
				}
			},

			async doBinQuery() {
				try {
					await this.$store.dispatch('cardPayment/fetchCardBrand');
					this.cardError = '';
					return true;
				} catch (error) {
					logEvent({
					category: 'BinQuery',
					action: 'Do Bin Query',
					label: `[BIN QUERY ERROR]: ${error}`,
					});

					this.cardError = this.$ml.get(this.binQueryError);

					return false;
				}
			},

			cancel() {
				this.$store.commit('payment/setNumberOfInstallments', 0);
			},

			isCardExpirationValid() {
				let month = this.$store.state.cardPayment.Expiration.Month || 0;
				let year = this.$store.state.cardPayment.Expiration.Year || 0;

				if (month === 0) return false;
				if (month > 12) return false;

				let today = new Date();
				let cardExpiration = new Date(`20${year}`, month);

				return cardExpiration >= today;
			},

			selectItem(item) {
				this.numberOfInstallments = item;
			},

			getLangOptions(item) {
				let tokens = [];

				tokens.push(new Token('quantity', item.Value));
				tokens.push(new Token('installmentPrice', item.installmentPrice));

				return new Options('installmentsItem', tokens);
			},

			async goToNextStep() {
				if (this.isCardExpirationValid() === false) {
					return Swal.fire('Oopss...', this.$ml.get('invalidExpirationDate'), 'error');
				}

				logEvent({
					category: 'checkout',
					action: 'forma_pagamento_cartao',
					label: 'Usuário preenche os dados do cartao para a forma de pagamento cartão de crédito, débito'
				});

				this.$store.dispatch('pipelineV2/pushNext');

				return;
			},

			async goToPreviousStep() {
				this.cancel();
				this.$store.dispatch('pipelineV2/pushPrevious');
				return;
			},
		},
	};
</script>

<style lang="scss" scoped>
	.card-data {
		display: flex;
		flex-direction: column;
		gap: 20px;
		margin-bottom: 20px;

		div {
			width: 100%;
		}

		.cielo-input {
			margin-bottom: 0px;
		}

		&__input-container {

			div {
				margin-bottom: 0 !important;
			}
		}

		&__container-security-card {
			display: flex;
			gap: 20px;
			align-items: flex-start;
			flex: 1 0 0;

			@media screen and (max-width: 1024px) {
				flex-direction: column;
				width: 100%;

				div {
					margin-bottom: 0 !important;
				}
			}

		}

		&__title {
			display: block;
			font-size: 16px;
			font-style: normal;
			font-weight: 600;
			line-height: 24px;
			color: #2f363e;
			margin: 0 0 0 0;
		}

		&__text {
			font-size: 14px;
			font-weight: 500;
			color: #5C6A7B;
			margin-top: -8px;
		}

		&__btn {
			display: flex;
		}

		&__billing-container {
			border-top: 2px solid #DBE0E5;
			padding-top: 20px;

			> *:not(:last-child) {
				margin-bottom: 20px;
			}
		}
	}
</style>

